import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {
  MarketingDetailCategoryInterface,
  MarketingDetailGroupInterface,
  MarketingDetailItemInterface,
  SavingsHeaderTextEnum,
  SavingsSourceEnum
} from '../../../models/marketing-detail.model';
import {FOOTER_GROUP_NAME, DEFAULT_FOOTER_VALUE, CATEGORY_SAVINGS_MARKER} from '../../../constants/marketing-detail.constants';
import {resolveXrayExamStatement} from '../../../utils/xray-exam-statement.util';
import {CategorySavingsSplitInterface, splitCategorySavings} from '../../../utils/category-savings/category-savings.util';

@Component({
  selector: 'app-common-dental-savings',
  templateUrl: './common-dental-savings.component.html',
  styleUrls: ['./common-dental-savings.component.scss'],
  standalone: false
})
export class CommonDentalSavingsComponent implements OnChanges {
  @Input() public commonDentalPlanSavings: MarketingDetailCategoryInterface | null = null;

  public readonly FOOTER_GROUP: string = FOOTER_GROUP_NAME;
  public readonly DEFAULT_FOOTER: string = DEFAULT_FOOTER_VALUE;
  public footer: string = DEFAULT_FOOTER_VALUE;

  public get isFeeScheduleView(): boolean {
    return this.commonDentalPlanSavings?.savingsSource === SavingsSourceEnum.FEE_SCHEDULE;
  }

  public getShowAdaCode(): boolean {
    return !this.isFeeScheduleView;
  }

  public getCdpsColumnClass(): string {
    return this.isFeeScheduleView ? 'col-6' : 'col-4';
  }

  public getMemberColumnLabel(): string {
    if (this.isFeeScheduleView) {
      return 'Member Savings*';
    }
    if (this.commonDentalPlanSavings?.savingsHeaderText === SavingsHeaderTextEnum.MEMBER_SAVES) {
      return 'Member Saves*';
    }
    return 'Member Pays*';
  }

  public getXrayExamStatement(): string {
    return resolveXrayExamStatement(this.commonDentalPlanSavings?.xrayExamFooterText);
  }

  public getSplit(group: MarketingDetailGroupInterface): CategorySavingsSplitInterface<MarketingDetailItemInterface> {
    return splitCategorySavings(group && group.items, (item) => item.description === CATEGORY_SAVINGS_MARKER, (item) => item.memberPays || null);
  }

  public hasAnyVisibleItems(): boolean {
    const groups: MarketingDetailGroupInterface[] | undefined = this.commonDentalPlanSavings?.groups;
    if (!groups) { return false; }
    return groups.some((group) => group.itemName !== FOOTER_GROUP_NAME && this.getSplit(group).visible.length > 0);
  }

  public headerCellClasses(hasSavings: boolean, isRight: boolean): {[key: string]: boolean} {
    const anyVisible: boolean = this.hasAnyVisibleItems();
    const showAda: boolean = this.getShowAdaCode();
    // Section name spans everything up to the memberPays column; the CategorySavings value sits in the
    // same column as the row-level memberPays so it's visually aligned with the data below.
    //   3-col layout (ADA / Description / MemberPays): name = col-8, value = col-4
    //   2-col layout (Description / MemberPays):       name = col-6, value = col-6
    // In the 2-col layout nameSpan === valueSpan, so building the ngClass map imperatively avoids
    // the duplicate-key overwrite that leaves both divs without a column class and wraps the value
    // onto a new line.
    const nameSpan: string = showAda ? 'col-8' : 'col-6';
    const valueSpan: string = showAda ? 'col-4' : 'col-6';
    const classes: {[key: string]: boolean} = {
      'section-header': anyVisible,
      'mt-3': anyVisible,
      'mb-2': anyVisible,
      'mb-3': !anyVisible
    };
    if (hasSavings && !isRight) classes[nameSpan] = true;
    if (!hasSavings && !isRight) classes['col-12'] = true;
    if (isRight) classes[valueSpan] = true;
    return classes;
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['commonDentalPlanSavings']) {
      this._extractFooter();
    }
  }

  private _extractFooter(): void {
    this.footer = DEFAULT_FOOTER_VALUE;

    if (!this.commonDentalPlanSavings?.groups) {
      return;
    }

    const footerGroup: MarketingDetailGroupInterface | undefined = this.commonDentalPlanSavings.groups
      .find((group: MarketingDetailGroupInterface) => group.itemName === FOOTER_GROUP_NAME);

    if (footerGroup && footerGroup.items.length) {
      this.footer = footerGroup.items[0].description;
    }
  }
}
